import type { UsAccountView, UsTrackerRead } from './usTrackerFeed';
import type { VenueReadiness } from './venueReadiness';

/** The US read failed with nothing to show: the code (or reason) for the hover,
 * the card's Retry (null when the coverage notice carries it) and whether a
 * re-read of the failed read is in flight. */
export type UsListsUnavailable = { code: string; retry: (() => void) | null; retrying: boolean };

const errored = (a: UsAccountView) => a.openError !== null || a.readError !== null;

/** Re-open a refused context, re-read a failed one. */
const retryAccounts = (accounts: readonly UsAccountView[]) => () => {
  for (const a of accounts) {
    if (a.openError !== null) a.actions.retryOpen();
    else a.actions.refresh();
  }
};

/**
 * #1238 item 5a — whether the Tracker's US read is "unavailable" (the K/P
 * word) rather than empty. Only when nothing US can be shown because it
 * failed: the feed's account list failed, or every own-view account is
 * mounted with nothing read and an error. A desk-only login, a list or
 * account still loading, a failed refresh that keeps its rows and a partial
 * failure beside readable accounts are not "unavailable".
 *
 * `latchedCode` is the code shown on the previous render. A failure being
 * retried (the list re-loading or the accounts re-opening after Retry, or the
 * feed's 10 s re-read, which clears each account's error while in flight) is
 * held as `retrying`, so the card never flips to "Reading…" and its Retry never
 * unmounts under the pointer (the #1060 pattern, held Tracker-side). It lets go
 * as soon as any account has a read to show.
 *
 * The Retry is explicit because the 10 s cadence re-reads each account's
 * portfolio and never re-lists or re-opens: a failed list is permanent for the
 * visit without it.
 */
export function usListsUnavailable(read: UsTrackerRead | null, latchedCode: string | null = null): UsListsUnavailable | null {
  if (!read) return null;
  if (read.list.state === 'failed') return { code: read.list.code, retry: read.retryList, retrying: false };
  // The list re-loading after a failure (its Retry was pressed): held, so the
  // button stays where it was pressed, with focus (#1060).
  if (read.list.state === 'loading') return latchedCode === null ? null : { code: latchedCode, retry: read.retryList, retrying: true };
  if (read.ownAccounts === 0 || read.accounts.length < read.ownAccounts) {
    // Readers mount one after another once the list answers; a failure being
    // retried stays held until each has reported.
    return latchedCode === null || read.ownAccounts === 0 ? null : { code: latchedCode, retry: read.retryList, retrying: true };
  }
  const accounts = read.accounts;
  if (accounts.every((a) => a.snapshot === null && !a.opening && !a.loading && errored(a))) {
    const first = accounts[0];
    return { code: first.openError ?? first.readError ?? 'US_READ_UNAVAILABLE', retry: retryAccounts(accounts), retrying: false };
  }
  // A failure being retried (Retry pressed, or the 10 s re-read): nothing to
  // show yet, and every account re-opening, re-reading or still failed.
  if (latchedCode !== null && accounts.some((a) => a.opening || a.loading)
    && accounts.every((a) => a.snapshot === null && (a.opening || a.loading || errored(a)))) {
    return { code: latchedCode, retry: retryAccounts(accounts), retrying: true };
  }
  return null;
}

/** #1238 5a — the page's own account-list read (`useUsTrackerReadiness`)
 * failed, so the feed never mounts: the cards still say "unavailable". The
 * coverage notice carries this read's Retry, so the cards add none. */
export function usReadinessUnavailable(readiness: VenueReadiness): UsListsUnavailable | null {
  if (readiness.state !== 'failed') return null;
  return { code: readiness.reason ?? 'US_READ_UNAVAILABLE', retry: null, retrying: false };
}
